import { useState, type ReactNode } from 'react';
import ChartBlock, { tryParseChart } from './ChartBlock';
import SpreadsheetBlock, { tryParseSpreadsheet } from './SpreadsheetBlock';
import DocumentBlock, { tryParseDocument } from './DocumentBlock';
import InfographicBlock, { tryParseInfographic } from './InfographicBlock';

interface CodeBlockProps {
  className?: string;
  children?: ReactNode;
}

function extractText(node: ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (node && typeof node === 'object' && 'props' in node) {
    return extractText((node as { props: { children?: ReactNode } }).props.children);
  }
  return '';
}

export default function CodeBlock({ className, children }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const match = /language-(\w+)/.exec(className ?? '');
  const language = match?.[1] ?? '';
  const text = extractText(children).replace(/\n$/, '');

  // Check if this is a chart block
  if (language === 'chart') {
    const chart = tryParseChart(text);
    if (chart) return <ChartBlock config={chart} />;
    if (looksComplete(text)) return <FailedCard label="Chart could not be loaded" />;
    return <PreparingCard label="Preparing your chart" />;
  }

  // Fallback: check JSON blocks for chart data
  if (language === 'json') {
    const chart = tryParseChart(text);
    if (chart) return <ChartBlock config={chart} />;
  }

  // Check if this is a spreadsheet download block
  if (language === 'spreadsheet') {
    const spreadsheet = tryParseSpreadsheet(text);
    if (spreadsheet) return <SpreadsheetBlock config={spreadsheet} />;

    // If text looks like complete JSON but failed to parse, show error state
    if (looksComplete(text)) return <FailedCard label="Spreadsheet could not be loaded" />;
    return <PreparingCard label="Preparing your spreadsheet" />;
  }

  // Check if this is a document download block
  if (language === 'document') {
    const doc = tryParseDocument(text);
    if (doc) return <DocumentBlock config={doc} />;

    // If text looks like complete JSON but failed to parse, show error state
    if (looksComplete(text)) return <FailedCard label="Document could not be loaded" />;
    return <PreparingCard label="Preparing your document" />;
  }

  // Check if this is an infographic block
  if (language === 'infographic') {
    const infographic = tryParseInfographic(text);
    if (infographic) return <InfographicBlock config={infographic} />;
    if (looksComplete(text)) return <FailedCard label="Infographic could not be loaded" />;
    return <PreparingCard label="Preparing your infographic" />;
  }

  // Default code block rendering
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="my-2 overflow-hidden rounded-md border border-white/10">
      <div className="flex items-center justify-between bg-[#2d2d2d] px-4 py-1.5 text-xs text-gray-400">
        <span>{language || 'text'}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1 rounded px-2 py-0.5 hover:bg-white/10 hover:text-gray-200 transition-colors"
        >
          {copied ? (
            <>
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Copied
            </>
          ) : (
            <>
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
              </svg>
              Copy
            </>
          )}
        </button>
      </div>
      <pre className="!mt-0 !rounded-t-none">
        <code className={className}>{children}</code>
      </pre>
    </div>
  );
}

/** Check if text looks like a complete JSON object (not still streaming) */
function looksComplete(text: string): boolean {
  const t = text.trim();
  return t.startsWith('{') && t.endsWith('}');
}

function PreparingCard({ label }: { label: string }) {
  return (
    <div className="my-3 rounded-lg border border-border-light bg-bg-secondary p-3">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#03A9F4]/10">
          <svg className="h-5 w-5 text-[#03A9F4] animate-plane-bob" viewBox="0 0 24 24" fill="currentColor">
            <path d="M21 16v-2l-8-5V3.5A1.5 1.5 0 0 0 11.5 2 1.5 1.5 0 0 0 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
          </svg>
        </div>
        <div>
          <p className="text-sm font-semibold text-text-primary font-heading">{label}</p>
          <p className="text-xs text-text-secondary">This may take a moment...</p>
        </div>
      </div>
    </div>
  );
}

function FailedCard({ label }: { label: string }) {
  return (
    <div className="my-3 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/30 dark:bg-red-950/20">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/30">
          <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <div>
          <p className="text-sm font-semibold text-text-primary font-heading">{label}</p>
          <p className="text-xs text-text-secondary">The data could not be processed. Try asking again.</p>
        </div>
      </div>
    </div>
  );
}
